<template>
  <div :class="['cc-summary-outer', { collapsed }]">
    <div class="cc-summary-pane">
      <div class="cc-sp-collapse-btn" :title="collapsed ? 'Expand panel' : 'Collapse panel'" @click="collapsed = !collapsed">
        {{ collapsed ? '▶' : '◀' }}
      </div>

      <div class="cc-sp-collapsed-strip">
        <div v-for="(label, i) in labels" :key="'icon-' + i" :class="['cc-sp-icon', stepStatus(i + 1)]" :title="`${label}: ${stepStatus(i + 1)}`">{{ i + 1 }}</div>
      </div>

      <div class="cc-sp-main-content">
        <div class="cc-sp-hdr">
          <div class="cc-sp-title">Template Summary</div>
          <div class="cc-sp-badge">{{ badge }}</div>
        </div>

        <!-- WhatsApp-style preview, refreshed with every draft and edit -->
        <div class="tp-preview">
          <div class="tp-preview-head">Preview<span v-if="summary.previewLanguage"> · {{ summary.previewLanguage }}</span></div>
          <div class="tp-phone">
            <div v-if="summary.body || summary.header" class="tp-bubble">
              <media-preview
                v-if="summary.header && summary.header.file"
                compact
                :show-caption="false"
                :format="summary.header.format"
                :media-id="summary.header.file.mediaId"
                :media-name="summary.header.file.mediaName || ''"
                :country-id="summary.header.countryId"
                :language-id="summary.header.file.languageId"
              />
              <div v-else-if="summary.header" class="tp-header-placeholder"><i :class="['fa', headerIcon]"></i> {{ summary.header.format.toLowerCase() }} header</div>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <p v-if="summary.body" class="tp-body" v-html="bodyHtml"></p>
              <div class="tp-time">{{ now }}</div>
            </div>
            <div v-else class="tp-empty">The message appears here once it's drafted.</div>
            <div v-for="(button, i) in summary.buttons || []" :key="i" class="tp-button">
              <i v-if="summary.buttonType === 'url_button'" class="fa fa-external-link"></i>
              <i v-else-if="summary.buttonType === 'phone_button'" class="fa fa-phone"></i>
              <i v-else class="fa fa-reply"></i>
              {{ button }}
            </div>
          </div>
        </div>

        <div class="cc-sp-section">
          <div class="cc-sp-lbl"><span :class="['cc-sn', stepStatus(1)]">1</span> Purpose</div>
          <div v-if="summary.purpose" class="cc-sp-row"><span class="cc-sp-k">Type</span><span class="cc-sp-v">{{ summary.purpose }}</span></div>
          <div v-else class="cc-sp-empty">Not selected yet</div>
        </div>

        <div class="cc-sp-section">
          <div class="cc-sp-lbl"><span :class="['cc-sn', stepStatus(2)]">2</span> Where it lives</div>
          <div v-if="list(summary.customers)" class="cc-sp-row"><span class="cc-sp-k">Customers</span><span class="cc-sp-v">{{ list(summary.customers) }}</span></div>
          <div v-if="list(summary.countries)" class="cc-sp-row"><span class="cc-sp-k">Countries</span><span class="cc-sp-v">{{ list(summary.countries) }}</span></div>
          <div v-if="list(summary.accounts)" class="cc-sp-row"><span class="cc-sp-k">WhatsApp accounts</span><span class="cc-sp-v">{{ list(summary.accounts) }}</span></div>
          <div v-if="summary.countries && summary.countries.length" class="cc-sp-row"><span class="cc-sp-k">Journey</span><span class="cc-sp-v">{{ summary.journey || 'None' }}</span></div>
          <div v-if="list(summary.languages)" class="cc-sp-row"><span class="cc-sp-k">Languages</span><span class="cc-sp-v">{{ list(summary.languages) }}</span></div>
          <div v-if="!list(summary.countries) && !list(summary.customers)" class="cc-sp-empty">Not configured yet</div>
        </div>

        <div class="cc-sp-section">
          <div class="cc-sp-lbl"><span :class="['cc-sn', stepStatus(3)]">3</span> Content</div>
          <div v-if="summary.header" class="cc-sp-row"><span class="cc-sp-k">Header</span><span class="cc-sp-v">{{ summary.header.format.toLowerCase() }}{{ summary.header.file ? `: ${summary.header.file.mediaName}` : '' }}</span></div>
          <div v-if="summary.buttons && summary.buttons.length" class="cc-sp-row"><span class="cc-sp-k">Buttons</span><span class="cc-sp-v">{{ summary.buttons.join(', ') }}</span></div>
          <div v-if="summary.body" class="cc-sp-row"><span class="cc-sp-k">Body</span><span class="cc-sp-v" style="color:#2E7D32">✓ Drafted</span></div>
          <div v-if="!summary.body" class="cc-sp-empty">Not drafted yet</div>
        </div>

        <div class="cc-sp-section">
          <div class="cc-sp-lbl"><span :class="['cc-sn', stepStatus(4)]">4</span> Name &amp; checks</div>
          <div v-if="summary.name" class="cc-sp-row"><span class="cc-sp-k">Name</span><span class="cc-sp-v tp-mono">{{ summary.name }}</span></div>
          <div v-if="summary.category" class="cc-sp-row"><span class="cc-sp-k">Category</span><span class="cc-sp-v">{{ summary.category === 'MARKETING' ? 'Marketing' : 'Utility' }}</span></div>
          <div v-if="summary.checks" class="cc-sp-row"><span class="cc-sp-k">Checks</span><span class="cc-sp-v" :style="{ color: summary.checks === 'Passed' ? '#2E7D32' : '#b42318' }">{{ summary.checks }}</span></div>
          <div v-if="!summary.name && !summary.checks" class="cc-sp-empty">Not done yet</div>
        </div>

        <div class="cc-sp-section">
          <div class="cc-sp-lbl"><span :class="['cc-sn', stepStatus(5)]">5</span> Submit</div>
          <div v-if="summary.submitted" class="cc-sp-row"><span class="cc-sp-k">Sent</span><span class="cc-sp-v">{{ summary.submitted.submitted }} of {{ summary.submitted.total }}</span></div>
          <div v-if="summary.submitted && summary.submitted.failed" class="cc-sp-row"><span class="cc-sp-k">To retry</span><span class="cc-sp-v" style="color:#b42318">{{ summary.submitted.failed }}</span></div>
          <div v-if="!summary.submitted" class="cc-sp-empty">Not submitted yet</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MediaPreview from '../../common/MediaPreview.vue'
import { TEMPLATE_STEP_LABELS } from '../constants'

const escapeHtml = s => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

export default {
  name: 'TemplateSummarySidebar',
  components: { MediaPreview },
  props: {
    currentStep: { type: Number, default: 1 },
    // context.summary from templateChat.js
    summary: { type: Object, default: () => ({}) }
  },
  data () {
    return { collapsed: false, labels: TEMPLATE_STEP_LABELS }
  },
  computed: {
    badge () {
      if (this.summary.submitted) return this.summary.submitted.failed ? 'Partly sent' : 'Sent for review'
      return 'In progress'
    },
    headerIcon () {
      const format = this.summary.header && this.summary.header.format
      return format === 'VIDEO' ? 'fa-video-camera' : format === 'DOCUMENT' ? 'fa-file-pdf-o' : 'fa-picture-o'
    },
    // WhatsApp formatting (*bold*, _italic_) and variables shown as chips.
    bodyHtml () {
      return escapeHtml(this.summary.body)
        .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
        .replace(/_([^_\n]+)_/g, '<em>$1</em>')
        .replace(/\{\{\s*(\d+)\s*\}\}/g, '<span class="tp-var">{{$1}}</span>')
        .replace(/\n/g, '<br>')
    },
    now () {
      return new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    }
  },
  methods: {
    stepStatus (stepNumber) {
      if (stepNumber < this.currentStep) return 'done'
      if (stepNumber === this.currentStep) return 'active'
      return 'pending'
    },
    list (items) {
      return items && items.length ? items.join(', ') : ''
    }
  }
}
</script>
<style scoped src="../create-campaign/summary-sidebar/CampaignSummarySidebar.css"></style>
<style scoped>
.tp-preview { margin: 0 0 12px; }
.tp-preview-head { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #667085; margin-bottom: 6px; }
.tp-phone { background: #efeae2; border-radius: 10px; padding: 12px 10px; display: flex; flex-direction: column; gap: 4px; }
.tp-bubble { background: #fff; border-radius: 0 8px 8px 8px; padding: 6px 8px 4px; box-shadow: 0 1px 1px rgba(0, 0, 0, 0.08); max-width: 100%; }
.tp-header-placeholder { background: #f2f4f7; color: #667085; border-radius: 6px; padding: 18px 8px; text-align: center; font-size: 12px; margin-bottom: 6px; text-transform: capitalize; }
.tp-body { margin: 4px 0 0; font-size: 12.5px; color: #111b21; line-height: 1.45; word-break: break-word; }
.tp-body >>> .tp-var { background: #e0f2f1; color: #0e7c72; border-radius: 4px; padding: 0 3px; font-family: monospace; font-size: 11.5px; }
.tp-time { text-align: right; font-size: 10px; color: #8696a0; margin-top: 2px; }
.tp-button { background: #fff; border-radius: 8px; padding: 6px; text-align: center; color: #027eb5; font-size: 12.5px; box-shadow: 0 1px 1px rgba(0, 0, 0, 0.08); }
.tp-button .fa { margin-right: 4px; }
.tp-empty { font-size: 12px; color: #8696a0; text-align: center; padding: 14px 4px; }
.tp-mono { font-family: monospace; font-size: 11px; word-break: break-all; }
</style>



// WEBPACK FOOTER //
// TemplateSummarySidebar.vue?17119c31